import { useEffect, useState, type HTMLAttributes, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import { formatClock, formatDateTime, formatRelative, formatRemaining, shortId } from '@/lib/format';
import { Icon } from './Icon';
import { DOMAIN_LABELS, statusDef, type StatusDomain, type Tone } from './status';

/** Count badge, the only pill. `attention` uses the accent: the count is work waiting for this person. */
export function Badge({
  value,
  label,
  attention = false,
  className,
}: {
  value: number;
  /** Screen reader text after the number, e.g. "drafts to approve". */
  label?: string;
  attention?: boolean;
  className?: string;
}) {
  return (
    <span className={cx('badge', attention && 'badge-accent', className)}>
      <span aria-hidden={label ? true : undefined}>{value > 999 ? '999+' : value}</span>
      {label ? <span className="visually-hidden">{`${value} ${label}`}</span> : null}
    </span>
  );
}

export type StatusDotProps =
  | {
      /** A contract state, labelled and toned by status.ts. */
      domain: StatusDomain;
      value: string | null | undefined;
      /** Prefix with the field name, e.g. "Stage Engaged", where several states sit side by side. */
      labelled?: boolean;
      tone?: never;
      children?: never;
      title?: string;
      className?: string;
    }
  | {
      domain?: never;
      value?: never;
      labelled?: never;
      /** Free text state, e.g. "Enabled". */
      children: ReactNode;
      tone?: Tone;
      title?: string;
      className?: string;
    };

/** State as text with a small dot: ink (current), hollow (finished) or accent (needs you). */
export function StatusDot(props: StatusDotProps) {
  if (props.domain) {
    const def = statusDef(props.domain, props.value);
    return (
      <span className={cx('status', def.tone !== 'neutral' && `status-${def.tone}`, props.className)} title={props.title ?? def.hint}>
        {props.labelled ? <span className="status-label">{DOMAIN_LABELS[props.domain]}</span> : null}
        {def.label}
        {def.hint ? <span className="visually-hidden">. {def.hint}</span> : null}
      </span>
    );
  }
  const tone = props.tone ?? 'neutral';
  return (
    <span className={cx('status', tone !== 'neutral' && `status-${tone}`, props.className)} title={props.title}>
      {props.children}
    </span>
  );
}

/**
 * A titled block separated from its siblings by a hairline. Layout by whitespace,
 * never a box: put sections inside one Panel (or the settings body), never Panel inside Section.
 */
export function Section({
  title,
  actions,
  children,
  footer,
  className,
  headingLevel = 2,
  ...rest
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  headingLevel?: 2 | 3;
} & Omit<HTMLAttributes<HTMLElement>, 'title'>) {
  const H = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <section className={cx('section', className)} {...rest}>
      {title || actions ? (
        <header className="section-header">
          {title ? <H>{title}</H> : <span />}
          {actions ? <div className="row">{actions}</div> : null}
        </header>
      ) : null}
      {children}
      {footer ? <footer className="section-footer">{footer}</footer> : null}
    </section>
  );
}

/** The white work surface (thread, draft card, list pane). One level only: no panel inside a panel. */
export function Panel({
  padded = false,
  className,
  as: As = 'div',
  ...rest
}: { padded?: boolean; as?: 'div' | 'section' | 'article' | 'aside' } & HTMLAttributes<HTMLElement>) {
  return <As className={cx('panel', padded && 'panel-pad', className)} {...rest} />;
}

/** Inline notice. `accent` only when the person must act or something may have gone wrong; say so in words. */
export function Banner({
  tone = 'neutral',
  title,
  children,
  actions,
  role,
}: {
  tone?: 'neutral' | 'accent';
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  role?: 'status' | 'alert';
}) {
  return (
    <div className={cx('banner', tone === 'accent' && 'banner-accent')} role={role}>
      <span className="banner-icon">
        <Icon name={tone === 'accent' ? 'alert' : 'info'} />
      </span>
      <div className="grow stack-sm" style={{ gap: 2 }}>
        {title ? <div className="banner-title">{title}</div> : null}
        {children ? <div>{children}</div> : null}
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </div>
  );
}

/** Opaque identifier in monospace with full value available to copy and to assistive tech. */
export function IdChip({ id, label }: { id: string | null | undefined; label?: string }) {
  if (!id) return <span className="unknown-value">None</span>;
  return (
    <span className="id-chip" title={id} aria-label={`${label ?? 'ID'} ${id}`}>
      {shortId(id)}
    </span>
  );
}

/** Absolute time with relative text. Renders the instant in a <time> element. */
export function Time({
  value,
  relative = true,
  timeZone,
  emptyLabel = 'Never',
}: {
  value: string | null | undefined;
  relative?: boolean;
  timeZone?: string;
  emptyLabel?: string;
}) {
  if (!value) return <span className="unknown-value">{emptyLabel}</span>;
  const abs = formatDateTime(value, timeZone);
  return (
    <time dateTime={value} title={abs}>
      {relative ? formatRelative(value) : abs}
    </time>
  );
}

/** Explicit missing value. Unknown, not applicable and not researched are different words, never zero. */
export function Unknown({ children = 'Unknown' }: { children?: ReactNode }) {
  return <span className="unknown-value">{children}</span>;
}

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(t);
  }, [intervalMs]);
  return now;
}

/**
 * Live countdown to a server supplied send time: "15:42 (in 58 min)" in the accent.
 * Reaching zero never triggers anything; it switches to `dueLabel`. The ticking text is
 * hidden from assistive tech; the absolute time is the accessible value.
 */
export function Countdown({
  dueAt,
  timeZone,
  dueLabel = 'Sending now',
  clockOffsetMs = 0,
}: {
  dueAt: string;
  timeZone?: string;
  dueLabel?: string;
  /** server_time minus browser time, so the countdown follows the server clock. */
  clockOffsetMs?: number;
}) {
  const now = useNow(1000) + clockOffsetMs;
  const remaining = (new Date(dueAt).getTime() - now) / 1000;
  return (
    <span className="countdown" title={formatDateTime(dueAt, timeZone)}>
      <time dateTime={dueAt}>{formatClock(dueAt, timeZone)}</time>{' '}
      <span aria-hidden>{remaining > 0 ? `(in ${formatRemaining(remaining)})` : `(${dueLabel.toLowerCase()})`}</span>
    </span>
  );
}

/** Small page heading (18px max). The top bar already says where you are; use only when the page needs a name. */
export function PageHeader({
  title,
  subtitle,
  actions,
  breadcrumb,
  meta,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  breadcrumb?: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div className="grow">
        {breadcrumb ? <nav className="breadcrumb" aria-label="Breadcrumb">{breadcrumb}</nav> : null}
        <h1 tabIndex={-1} data-page-heading>
          {title}
        </h1>
        {subtitle ? <p className="page-header-sub">{subtitle}</p> : null}
        {meta ? <div className="row" style={{ marginTop: 'var(--space-2)' }}>{meta}</div> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

/** Two-column key/value facts. */
export function DescriptionList({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <dl className="dl">
      {items.map(([k, v], i) => (
        <div key={i} style={{ display: 'contents' }}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
